import {
  accumulatedTranslate,
  elementBounds,
  type AuthoredPoint,
  type AuthoredRect,
} from './svgElementGeometry';

const SVG_NUMBER = /[-+]?(?:\d*\.\d+|\d+)(?:e[-+]?\d+)?/gi;

function numbersFrom(value: string): number[] {
  return (value.match(SVG_NUMBER) ?? []).map(Number).filter((value) => Number.isFinite(value));
}

function normalizePoint(point: AuthoredPoint, bounds: AuthoredRect): AuthoredPoint {
  return {
    x: (point.x - bounds.x) / bounds.width,
    y: (point.y - bounds.y) / bounds.height,
  };
}

function formatPoint(point: AuthoredPoint): string {
  const round = (value: number) => Number(value.toFixed(6));
  return `${round(point.x)} ${round(point.y)}`;
}

function absolutePathPoints(element: Element, root: Element): AuthoredPoint[] {
  const origin = accumulatedTranslate(element, root);
  const values = numbersFrom(element.getAttribute('d') ?? '');
  const points: AuthoredPoint[] = [];
  for (let index = 0; index + 1 < values.length; index += 2) {
    points.push({ x: origin.x + values[index], y: origin.y + values[index + 1] });
  }
  return points;
}

function absolutePolygonPoints(element: Element, root: Element): AuthoredPoint[] {
  const origin = accumulatedTranslate(element, root);
  const values = numbersFrom(element.getAttribute('points') ?? '');
  const points: AuthoredPoint[] = [];
  for (let index = 0; index + 1 < values.length; index += 2) {
    points.push({ x: origin.x + values[index], y: origin.y + values[index + 1] });
  }
  return points;
}

function closedPathMarkup(points: AuthoredPoint[], bounds: AuthoredRect): string {
  if (points.length < 3) return '';
  const normalized = points.map((point) => normalizePoint(point, bounds));
  return `<path d="M ${normalized.map(formatPoint).join(' L ')} Z"/>`;
}

function circleMarkup(element: Element, root: Element, bounds: AuthoredRect): string | null {
  const origin = accumulatedTranslate(element, root);
  const cx = Number(element.getAttribute('cx') ?? 0);
  const cy = Number(element.getAttribute('cy') ?? 0);
  const radius = Number(element.getAttribute('r') ?? 0);
  if (!Number.isFinite(cx) || !Number.isFinite(cy) || !Number.isFinite(radius) || radius <= 0) {
    return null;
  }
  const centre = normalizePoint({ x: origin.x + cx, y: origin.y + cy }, bounds);
  const rx = radius / bounds.width;
  const ry = radius / bounds.height;
  return `<ellipse cx="${centre.x}" cy="${centre.y}" rx="${rx}" ry="${ry}"/>`;
}

/**
 * Build clip-path markup in objectBoundingBox space (0–1 relative to the
 * painted glass image). Rect guides return undefined so callers clip to the
 * full slot image bounds.
 */
export function buildGlazingClipMarkup(
  element: Element,
  root: Element,
  bounds: AuthoredRect
): string | undefined {
  const tag = element.tagName.toLowerCase();
  if (tag === 'rect') return undefined;
  if (bounds.width <= 0 || bounds.height <= 0) return undefined;

  if (tag === 'path') {
    const markup = closedPathMarkup(absolutePathPoints(element, root), bounds);
    return markup || undefined;
  }
  if (tag === 'polygon' || tag === 'polyline') {
    const markup = closedPathMarkup(absolutePolygonPoints(element, root), bounds);
    return markup || undefined;
  }
  if (tag === 'circle') {
    return circleMarkup(element, root, bounds) ?? undefined;
  }
  if (tag === 'ellipse') {
    const origin = accumulatedTranslate(element, root);
    const cx = Number(element.getAttribute('cx') ?? 0);
    const cy = Number(element.getAttribute('cy') ?? 0);
    const rx = Number(element.getAttribute('rx') ?? 0);
    const ry = Number(element.getAttribute('ry') ?? 0);
    if (!Number.isFinite(cx) || !Number.isFinite(cy) || rx <= 0 || ry <= 0) return undefined;
    const centre = normalizePoint({ x: origin.x + cx, y: origin.y + cy }, bounds);
    return `<ellipse cx="${centre.x}" cy="${centre.y}" rx="${rx / bounds.width}" ry="${ry / bounds.height}"/>`;
  }
  return undefined;
}

/** @deprecated Use buildGlazingClipMarkup with slot bounds. */
export function buildGlazingClipMarkupLegacy(element: Element, root: Element): string | undefined {
  const bounds = elementBounds(element, root);
  if (!bounds) return undefined;
  return buildGlazingClipMarkup(element, root, bounds);
}
